import React from 'react';
import {
  TRegistrationForm,
  TRegistrationFormValues,
} from './RegistrationForm.types';
import { useForm, Resolver, Controller } from 'react-hook-form';
import { getRegistrationSchema } from './RegistrationForm.schema';
import { yupResolver } from '@hookform/resolvers/yup';
import { REGISTRATION_FORM_DEFAULT_VALUES } from './RegistrationForm.constants';
import { Button, TextInput } from 'components';
import './RegistrationForm.styled.scss';
import { useAppDispatch, useAppSelector } from 'store/store';
import {
  googleAuthAsyncThunk,
  registerUserAsyncThunk,
} from 'store/users/users.action';

import { ModalLayout } from '../../molecules/ModalLayout/ModalLayout';
import { CredentialResponse, GoogleLogin } from '@react-oauth/google';
import { NOTIFICATION_TYPES } from 'store/notifications/notifications.types';
import { pushNotification } from 'store/notifications/notifications.slices';
import { useLocalize } from 'store/contexts/LocalizationContext/LocalizationContext.provider';

export const RegistrationForm: TRegistrationForm = ({ closeModal }) => {
  const localize = useLocalize();
  const dispatch = useAppDispatch();
  const { isLoading } = useAppSelector((state) => state.users.registerUser);

  const registrationSchema = React.useMemo(
    () => getRegistrationSchema(localize),
    [localize]
  );

  const { control, handleSubmit } = useForm<TRegistrationFormValues>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: REGISTRATION_FORM_DEFAULT_VALUES,
    resolver: yupResolver(
      registrationSchema
    ) as Resolver<TRegistrationFormValues>,
  });

  const onSubmit = (data: TRegistrationFormValues) => {
    dispatch(
      registerUserAsyncThunk({
        body: data,
        onSuccess: closeModal,
      })
    );
  };

  const onGoogleSuccess = (credentialResponse: CredentialResponse) => {
    dispatch(
      googleAuthAsyncThunk({
        body: { idToken: credentialResponse.credential },
        onSuccess: closeModal,
      })
    );
  };

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      className="registration-form__form"
      noValidate
    >
      <ModalLayout
        mainContent={
          <>
            <Controller
              control={control}
              name="email"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label={localize('general.label.email') as string}
                  placeholder={localize('general.placeholder.email') as string}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
            <Controller
              control={control}
              name="password"
              render={({ field, fieldState }) => (
                <TextInput
                  {...field}
                  label={localize('general.label.password') as string}
                  type="password"
                  placeholder={
                    localize('registration.form.password.placeholder') as string
                  }
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  required
                />
              )}
            />
            <GoogleLogin
              text="signup_with"
              theme="filled_black"
              shape="circle"
              onSuccess={onGoogleSuccess}
              onError={() => {
                dispatch(
                  pushNotification({
                    message: localize(
                      'auth.notification.login.failed'
                    ) as string,
                    type: NOTIFICATION_TYPES.ERROR,
                  })
                );
              }}
            />
          </>
        }
        footerContent={
          <div className="registration-form__actions">
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={isLoading}
            >
              {localize('registration.form.submit')}
            </Button>
          </div>
        }
      />
    </form>
  );
};
